/**
 * Club API module
 */

import { apiClient } from './client'

export interface ClubResponse {
  id: string
  name: string
  shortName?: string
  description?: string
  location?: string
  logo?: string
  sportId?: string
  sportName?: string
  website?: string
  linkText?: string
  availableEquipment?: string[]
  owner?: boolean
  admin?: boolean
  member?: boolean
  athlete?: boolean
  selector?: boolean
  staff?: boolean
  headcoach?: boolean
  assistantcoach?: boolean
  programcoach?: boolean
  support?: boolean
  ownerId?: string
  createdAt?: string
}

export interface ClubListResponse {
  clubs: ClubResponse[]
}

export interface ClubDetailsUpdateRequest {
  name: string
  logo?: string
  sportId?: string
  description?: string
  website?: string
  linkText?: string
  availableEquipment?: string[]
}

export interface ClubDetailsUpdateResponse {
  club: ClubResponse
}

export interface ClubPeakBody {
  id: string
  name: string
  sportId: string
}

export interface ClubPeakBodiesResponse {
  sportId: string | null
  peakBodies: ClubPeakBody[]
  availablePeakBodies: ClubPeakBody[]
}

export interface ClubHomeSeason {
  id: string
  name: string
  active?: boolean
  type?: string
  sportId?: string
  sportName?: string
  startDate?: string
  endDate?: string
  logo?: string
  cardImage?: string
}

export interface ClubHomeEventRoles {
  owner?: boolean
  admin?: boolean
  athlete?: boolean
  selector?: boolean
  support?: boolean
  headcoach?: boolean
  assistantcoach?: boolean
  programcoach?: boolean
}

export interface ClubHomeEventRegistration {
  status?:
    | 'none'
    | 'incomplete'
    | 'paymentRequired'
    | 'paymentPending'
    | 'pendingPayment'
    | 'complete'
    | 'alreadyRegistered'
  nextStep?: 'questions' | 'dataCollection' | 'payment' | 'confirmation'
  paymentUrl?: string
  redirectUrl?: string
  payment?: {
    status?: string
    redirectUrl?: string
  }
}

export interface ClubHomeEvent {
  id: string
  active?: boolean
  seasonId?: string
  name?: string
  title: string
  eventType?: string
  eventNoun?: string
  location?: string
  cardImage?: string
  onlineReg?: boolean
  roles?: ClubHomeEventRoles
  registration?: ClubHomeEventRegistration
  registrationStatus?: ClubHomeEventRegistration['status']
  registrationNextStep?: ClubHomeEventRegistration['nextStep']
  paymentRedirectUrl?: string
}

export interface ClubHomeResponse {
  club: ClubResponse
  seasons: ClubHomeSeason[]
  events: ClubHomeEvent[]
}

export class ClubAPI {
  async getClub(clubId: string, signal?: AbortSignal): Promise<ClubResponse> {
    return apiClient.get(`/clubs/${clubId}`, undefined, signal)
  }

  async listClubs(signal?: AbortSignal): Promise<ClubResponse[]> {
    return apiClient.get('/clubs', undefined, signal)
  }

  async listClubsForUser(
    userId: string,
    signal?: AbortSignal
  ): Promise<ClubListResponse> {
    return apiClient.get('/v2/club/loadAllClubsForUser', { userId }, signal)
  }

  async loadClubHome(
    clubId: string,
    signal?: AbortSignal
  ): Promise<ClubHomeResponse> {
    return apiClient.get(`/v2/club/${clubId}/loadClubHome`, undefined, signal)
  }

  async updateClubDetails(
    clubId: string,
    payload: ClubDetailsUpdateRequest,
    signal?: AbortSignal
  ): Promise<ClubDetailsUpdateResponse> {
    return apiClient.patch(
      `/v2/club/${clubId}/details`,
      payload,
      undefined,
      signal
    )
  }

  async loadPeakBodies(clubId: string, sportId?: string, signal?: AbortSignal): Promise<ClubPeakBodiesResponse> {
    return apiClient.get(`/v2/clubs/${clubId}/peak-bodies`, sportId ? { sportId } : undefined, signal)
  }

  async linkPeakBody(clubId: string, peakBodyId: string, signal?: AbortSignal): Promise<void> {
    await apiClient.post(`/v2/clubs/${clubId}/peak-bodies/${peakBodyId}`, {}, undefined, signal)
  }

  async unlinkPeakBody(clubId: string, peakBodyId: string, signal?: AbortSignal): Promise<void> {
    await apiClient.delete(`/v2/clubs/${clubId}/peak-bodies/${peakBodyId}`, undefined, signal)
  }
}

export const clubAPI = new ClubAPI()
